import { apiFetch, qs, send } from "@/lib/api-client";
import type {
	AdminAccount,
	AdminCredentials,
	AdminSession,
	AdminUser,
	AuditEntry,
	AuditTarget,
	CatalogPlan,
	Charge,
	CreditAdjustmentReason,
	CreditPack,
	CursorPage,
	FlowRunRow,
	Invoice,
	LedgerEntry,
	PackInput,
	Paged,
	PaymentsSummary,
	PlanInput,
	PlatformOverview,
	RoleInput,
	RunFilter,
	StripeEventFilter,
	StripeEventRow,
	StripeSync,
	TenantChannel,
	TenantDetail,
	TenantPost,
	TenantProject,
	TenantRow,
	TenantSort,
	TenantStatus,
	TenantTeam,
	UsageSummary,
	UserAccount,
	UserCredentials,
	UserDetail,
	UserRow,
	UserStatusFilter,
} from "./types";

// ── Auth ─────────────────────────────────────────────────────────────────────

export const login = (input: { email: string; password: string }) =>
	send<AdminSession>("/auth/admin/login", input);
export type AdminMe = { user: AdminUser; stripeMode: "live" | "test" | null };
export const meKey = "/admin/me";
export const getMe = () => apiFetch<AdminMe>(meKey);

// ── Overview ─────────────────────────────────────────────────────────────────

export const overviewKey = (days: number) => `/admin/overview${qs({ days })}`;

// ── Tenants ──────────────────────────────────────────────────────────────────

export type TenantQuery = {
	q?: string;
	plan?: string;
	status?: TenantStatus;
	sort?: TenantSort;
	page?: number;
};
export type TenantList = Paged & { tenants: TenantRow[] };

export const tenantsKey = (query: TenantQuery) =>
	`/admin/tenants${qs({
		q: query.q,
		plan: query.plan,
		status: query.status === "all" ? undefined : query.status,
		sort: query.sort === "newest" ? undefined : query.sort,
		page: query.page === 1 ? undefined : query.page,
	})}`;
export const tenantKey = (id: string) => `/admin/tenants/${id}`;
export const tenantUsageKey = (id: string, days: number) =>
	`/admin/tenants/${id}/usage${qs({ days })}`;
export const tenantInvoicesKey = (id: string) => `/admin/tenants/${id}/invoices`;

export const getTenantLedger = (id: string, cursor: string | null) =>
	apiFetch<CursorPage<"entries", LedgerEntry>>(
		`/admin/tenants/${id}/ledger${qs({ cursor, limit: 50 })}`,
	);
export const getTenantAudit = (id: string, cursor: string | null) =>
	apiFetch<CursorPage<"entries", AuditEntry>>(
		`/admin/tenants/${id}/audit${qs({ cursor, limit: 50 })}`,
	);

export const updateTenant = (id: string, patch: { name?: string; slug?: string }) =>
	send<{ name: string; slug: string }>(`/admin/tenants/${id}`, patch, "PATCH");
export const setTenantPlan = (
	id: string,
	input: { planId: string; seats?: number; grantDifference: boolean },
) =>
	send<{ planId: string; seats: number; grantedMillicredits: number }>(
		`/admin/tenants/${id}/plan`,
		input,
		"PUT",
	);
export const adjustTenantCredits = (
	id: string,
	input: { credits: number; reason: CreditAdjustmentReason; note: string },
) => send<{ balanceAfterMillicredits: number }>(`/admin/tenants/${id}/credits`, input);
export const setTenantSuspended = (id: string, input: { suspended: boolean; reason?: string }) =>
	send<{ suspendedAt: string | null; suspendedReason: string | null }>(
		`/admin/tenants/${id}/suspension`,
		input,
		"PUT",
	);

export type { Invoice, UsageSummary };

// ── Tenant team ──────────────────────────────────────────────────────────────

export const tenantTeamKey = (id: string) => `/admin/tenants/${id}/team`;

type Ok = { ok: true };

export const addTenantMember = (
	id: string,
	input: { userId: string; roleId: string; allowOverSeats: boolean },
) => send<{ memberId: string }>(`/admin/tenants/${id}/members`, input);
export const changeMemberRole = (id: string, memberId: string, roleId: string) =>
	send<Ok>(`/admin/tenants/${id}/members/${memberId}`, { roleId }, "PATCH");
export const removeMember = (id: string, memberId: string) =>
	apiFetch<Ok>(`/admin/tenants/${id}/members/${memberId}`, { method: "DELETE" });
export const transferOwnership = (id: string, memberId: string) =>
	send<Ok>(`/admin/tenants/${id}/owner`, { memberId });

export const createInvite = (
	id: string,
	input: { email: string; roleId: string; allowOverSeats: boolean },
) => send<{ inviteId: string; url: string }>(`/admin/tenants/${id}/invites`, input);
export const renewInviteLink = (id: string, inviteId: string) =>
	send<{ inviteId: string; url: string }>(`/admin/tenants/${id}/invites/${inviteId}/link`, {});
export const revokeInvite = (id: string, inviteId: string) =>
	apiFetch<Ok>(`/admin/tenants/${id}/invites/${inviteId}`, { method: "DELETE" });

export const createRole = (id: string, input: RoleInput) =>
	send<{ id: string }>(`/admin/tenants/${id}/roles`, input);
export const updateRole = (id: string, roleId: string, patch: Partial<RoleInput>) =>
	send<{ id: string }>(`/admin/tenants/${id}/roles/${roleId}`, patch, "PATCH");
export const deleteRole = (id: string, roleId: string, reassignTo?: string) =>
	apiFetch<Ok>(`/admin/tenants/${id}/roles/${roleId}${qs({ reassignTo })}`, {
		method: "DELETE",
	});

export type { TenantTeam };

// ── Tenant content ───────────────────────────────────────────────────────────

export const tenantProjectsKey = (id: string) => `/admin/tenants/${id}/projects`;
export const updateProject = (
	id: string,
	projectId: string,
	patch: { name?: string; archived?: boolean },
) => send<{ id: string }>(`/admin/tenants/${id}/projects/${projectId}`, patch, "PATCH");

export const getTenantRuns = (id: string, status: RunFilter, cursor: string | null) =>
	apiFetch<CursorPage<"runs", FlowRunRow>>(
		`/admin/tenants/${id}/runs${qs({ status: status === "all" ? undefined : status, cursor, limit: 50 })}`,
	);
export const cancelRun = (id: string, runId: string) =>
	send<{ status: string }>(`/admin/tenants/${id}/runs/${runId}/cancel`, {});

export type { TenantProject };

export const tenantChannelsKey = (id: string) => `/admin/tenants/${id}/channels`;
export const getTenantPosts = (id: string, accountId: string | null, cursor: string | null) =>
	apiFetch<CursorPage<"posts", TenantPost>>(
		`/admin/tenants/${id}/posts${qs({ accountId: accountId ?? undefined, cursor, limit: 30 })}`,
	);
export const importChannel = (id: string, accountId: string) =>
	send<{ queued: boolean }>(`/admin/tenants/${id}/channels/${accountId}/import`, {});

export type { TenantChannel, TenantPost };

// ── Users ────────────────────────────────────────────────────────────────────

export type UserQuery = { q?: string; status?: UserStatusFilter; page?: number };
export type UserList = Paged & { users: UserRow[] };

export const usersKey = (query: UserQuery) =>
	`/admin/users${qs({
		q: query.q,
		status: query.status === "all" ? undefined : query.status,
		page: query.page === 1 ? undefined : query.page,
	})}`;
export const userKey = (id: string) => `/admin/users/${id}`;
export const getUserAudit = (id: string, cursor: string | null) =>
	apiFetch<CursorPage<"entries", AuditEntry>>(
		`/admin/users/${id}/audit${qs({ cursor, limit: 50 })}`,
	);

export const createUser = (input: { name: string; email: string; password?: string }) =>
	send<UserCredentials>("/admin/users", input);
export const updateUser = (id: string, patch: { name?: string; email?: string }) =>
	send<UserAccount>(`/admin/users/${id}`, patch, "PATCH");
export const setUserDisabled = (id: string, input: { disabled: boolean; reason?: string }) =>
	send<UserAccount>(`/admin/users/${id}/disabled`, input, "PUT");
export const resetUserPassword = (id: string, input: { password?: string }) =>
	send<UserCredentials>(`/admin/users/${id}/password`, input);
export const revokeUserSessions = (id: string) =>
	send<UserAccount>(`/admin/users/${id}/sessions/revoke`, {});

export type { UserCredentials };

// ── Admins ───────────────────────────────────────────────────────────────────

export const adminsKey = "/admin/admins";
export const createAdmin = (input: { name: string; email: string }) =>
	send<AdminCredentials>("/admin/admins", input);
export const updateAdmin = (id: string, patch: { name?: string; email?: string }) =>
	send<AdminAccount>(`/admin/admins/${id}`, patch, "PATCH");
export const setAdminDisabled = (id: string, disabled: boolean) =>
	send<AdminAccount>(`/admin/admins/${id}/disabled`, { disabled }, "PUT");
export const resetAdminPassword = (id: string) =>
	send<AdminCredentials>(`/admin/admins/${id}/password`, {});
export const changePassword = (input: { currentPassword: string; newPassword: string }) =>
	send<{ ok: true }>("/admin/me/password", input, "PUT");

export type { AdminAccount, AdminCredentials };

// ── Catalog ──────────────────────────────────────────────────────────────────

export type Catalog = { billingConfigured: boolean; plans: CatalogPlan[]; packs: CreditPack[] };
export const catalogKey = "/admin/catalog";

export const createPlan = (input: PlanInput & { id: string }) =>
	send<{ plan: CatalogPlan; stripe: StripeSync }>("/admin/plans", input);
export const updatePlan = (id: string, patch: Partial<PlanInput>) =>
	send<{ plan: CatalogPlan; stripe: StripeSync }>(`/admin/plans/${id}`, patch, "PATCH");
export const createPack = (input: PackInput & { id: string }) =>
	send<{ pack: CreditPack; stripe: StripeSync }>("/admin/packs", input);
export const updatePack = (id: string, patch: Partial<PackInput>) =>
	send<{ pack: CreditPack; stripe: StripeSync }>(`/admin/packs/${id}`, patch, "PATCH");

// ── Payments ─────────────────────────────────────────────────────────────────

export const paymentsSummaryKey = (days: number) => `/admin/payments/summary${qs({ days })}`;
export const getCharges = (cursor: string | null, customer?: string) =>
	apiFetch<{ configured: boolean; charges: Charge[]; nextCursor: string | null }>(
		`/admin/payments/charges${qs({ cursor, customer, limit: 25 })}`,
	);
export type StripeEventList = Paged & { events: StripeEventRow[] };
export const stripeEventsKey = (filter: StripeEventFilter, page: number) =>
	`/admin/payments/events${qs({ filter: filter === "all" ? undefined : filter, page: page === 1 ? undefined : page })}`;

export type { PaymentsSummary, PlatformOverview, TenantDetail, UserDetail };

// ── Audit ────────────────────────────────────────────────────────────────────

export const getAudit = (cursor: string | null, target?: AuditTarget) =>
	apiFetch<CursorPage<"entries", AuditEntry>>(`/admin/audit${qs({ cursor, target, limit: 50 })}`);
